import type { AppKitNetwork } from '@reown/appkit/networks';

import {
    CRONOS_USDC_ADDRESS,
    CRONOS_USDC_NATIVE_ADDRESS,
    ROBINHOOD_USDG_ADDRESS,
    USDC_DECIMALS,
} from '@/lib/api/speed/constants';

import {
    CRONOS_CHAIN_ID,
    cronosNetwork,
    ROBINHOOD_CHAIN_ID,
    robinhoodNetwork,
} from './networks';

/**
 * The "authorization anchor" chains.
 *
 * An anchor is the chain a user's Snappy identity lives on: their
 * `CustodialWalletManager` wallet holds the account hashes, that manager is the
 * EIP-712 `verifyingContract` of every statement they sign (trading session,
 * owner binding, fund operation), and its `ServiceWalletRegistry` records the
 * bridge intents their withdrawals need. HyperEVM (999) is the TRADING chain and
 * is never an anchor.
 *
 * Cronos (25) was the only anchor until Robinhood Chain (4663) was added, so 25
 * stays the default: a request that names no anchor, and a stored session minted
 * before anchors existed, both mean Cronos. A user may hold an authorization
 * wallet on either chain, or on both, and picks which one a trading session is
 * anchored on.
 */

// re-exported so anchor consumers need only this module
export { CRONOS_CHAIN_ID, ROBINHOOD_CHAIN_ID };

export const ANCHOR_CHAIN_IDS = [CRONOS_CHAIN_ID, ROBINHOOD_CHAIN_ID] as const;

export type AnchorChainId = (typeof ANCHOR_CHAIN_IDS)[number];

/** What "no anchor was named" means, on the wire and in this app. */
export const DEFAULT_ANCHOR_CHAIN_ID: AnchorChainId = CRONOS_CHAIN_ID;

export function isAnchorChainId(
    value: number | string | null | undefined,
): value is AnchorChainId {
    return (ANCHOR_CHAIN_IDS as readonly unknown[]).includes(Number(value));
}

/**
 * The anchor's collateral token: what a deposit from that chain sends and what a
 * withdrawal to it pays out in. Both are 6 decimals, which is what every amount
 * in this app is expressed in.
 */
export interface AnchorStableToken {
    address: `0x${string}`;
    symbol: string;
    decimals: number;
}

export interface AnchorChainDescriptor {
    chainId: AnchorChainId;
    /** Name shown to the user. Short on purpose: it goes inline in sentences. */
    label: string;
    /** The AppKit/wagmi network, for chain switches. */
    network: AppKitNetwork;
    stable: AnchorStableToken;
}

export const ANCHOR_CHAINS: Record<AnchorChainId, AnchorChainDescriptor> = {
    [CRONOS_CHAIN_ID]: {
        chainId: CRONOS_CHAIN_ID,
        label: 'Cronos',
        network: cronosNetwork,
        stable: {
            address: CRONOS_USDC_ADDRESS,
            symbol: 'USDC.e',
            decimals: USDC_DECIMALS,
        },
    },
    [ROBINHOOD_CHAIN_ID]: {
        chainId: ROBINHOOD_CHAIN_ID,
        label: 'Robinhood',
        network: robinhoodNetwork,
        stable: {
            address: ROBINHOOD_USDG_ADDRESS,
            symbol: 'USDG',
            decimals: USDC_DECIMALS,
        },
    },
};

/**
 * The stables a bridged withdrawal may pay out in on each anchor, default first.
 * Cronos has two: USDC.e (bridged, the default everywhere in this app) and
 * Circle's native USDC. A bridge that cannot deliver USDC.e directly (LI.FI
 * bridges native USDC and refuses a destination swap) refuses the default, and
 * the way out is to withdraw USDC and swap it to USDC.e on WolfSwap, so the
 * form offers the second token beside the first. Mirrors the backend's
 * ANCHOR_WITHDRAW_ALTERNATES.
 */
export const ANCHOR_WITHDRAW_TOKENS: Record<
    AnchorChainId,
    readonly AnchorStableToken[]
> = {
    [CRONOS_CHAIN_ID]: [
        ANCHOR_CHAINS[CRONOS_CHAIN_ID].stable,
        {
            address: CRONOS_USDC_NATIVE_ADDRESS,
            symbol: 'USDC',
            decimals: USDC_DECIMALS,
        },
    ],
    [ROBINHOOD_CHAIN_ID]: [ANCHOR_CHAINS[ROBINHOOD_CHAIN_ID].stable],
};

/** The WolfSwap swap page pre-filled to turn `sell` into `buy` on `chainId`. */
export function wolfswapSwapHref(
    chainId: number,
    sell: `0x${string}`,
    buy: `0x${string}`,
): string {
    const params = new URLSearchParams({
        chainId: String(chainId),
        sellToken: sell,
        buyToken: buy,
    });
    return `https://wolfswap.app/swap?${params.toString()}`;
}

/** The anchor descriptor, or a hard failure: an unknown anchor must never sign. */
export function anchorChain(chainId: AnchorChainId): AnchorChainDescriptor {
    const descriptor = ANCHOR_CHAINS[chainId];
    if (!descriptor) {
        throw new Error(`Chain ${chainId} is not an authorization anchor.`);
    }
    return descriptor;
}

export function anchorLabel(chainId: AnchorChainId): string {
    return anchorChain(chainId).label;
}

/**
 * Narrow an arbitrary chain id to an anchor, falling back to the default. Use it
 * where a value is a HINT (a connected wallet's chain, a stored preference), not
 * where it decides what gets signed.
 */
export function toAnchorChainId(
    value: number | string | null | undefined,
    fallback: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): AnchorChainId {
    return isAnchorChainId(value) ? (Number(value) as AnchorChainId) : fallback;
}
